import React, { useEffect, useState } from "react";
import { HashRouter as Router, Routes, Route } from "react-router-dom";
import { FluentProvider, teamsLightTheme } from "@fluentui/react-components";
import * as microsoftTeams from "@microsoft/teams-js";
import WebAuthService from "../services/webAuthService";
import Tab from "./Tab";
import TabConfig from "./TabConfig";
import Privacy from "./Privacy";
import TermsOfUse from "./TermsOfUse";
import LoginPage from "./LoginPage";
import WebHeader from "./WebHeader";
import { WebUser } from "../services/webAuthService";

function isInTeams(): boolean {
  try {
    return typeof window !== "undefined" && (
      window.parent !== window ||
      /Teams/i.test(navigator.userAgent) ||
      window.location.search.includes("hostName=teams")
    );
  } catch {
    return false;
  }
}

function App() {
  const [initialized, setInitialized] = useState(false);
  const [inTeams, setInTeams] = useState(false);
  const [teamsDarkMode, setTeamsDarkMode] = useState(false);
  const [webAuthenticated, setWebAuthenticated] = useState(false);
  const [webUser, setWebUser] = useState<WebUser | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [webDarkMode, setWebDarkMode] = useState(() => localStorage.getItem("gantt-dark-mode") === "true");

  useEffect(() => {
    async function init() {
      if (isInTeams()) {
        // Teams path
        try {
          await microsoftTeams.app.initialize();
          setInTeams(true);
          // Detect Teams dark mode
          const ctx = await microsoftTeams.app.getContext();
          const theme = (ctx as any).app?.theme || (ctx as any).theme || "";
          if (theme === "dark") {
            setTeamsDarkMode(true);
          }
          // Listen for theme changes
          microsoftTeams.app.registerOnThemeChangeHandler((newTheme: string) => {
            setTeamsDarkMode(newTheme === "dark");
          });
          microsoftTeams.app.notifySuccess();
        } catch {}
        setInitialized(true);
        setCheckingAuth(false);
      } else {
        // Web path — check for MSAL redirect first
        try {
          const authService = WebAuthService.getInstance();
          const redirectUser = await authService.handleRedirect();
          if (redirectUser) {
            setWebUser(redirectUser);
            setWebAuthenticated(true);
            setInitialized(true);
            setCheckingAuth(false);
            return;
          }
        } catch (err) {
          console.error("MSAL redirect handling failed:", err);
        }

        // Check existing session
        const authService = WebAuthService.getInstance();
        if (authService.isAuthenticated()) {
          const user = await authService.validateToken();
          if (user) {
            setWebUser(user);
            setWebAuthenticated(true);
          }
        }

        setInitialized(true);
        setCheckingAuth(false);
      }
    }
    init();
  }, []);

  if (!initialized || checkingAuth) {
    const savedDark = localStorage.getItem("gantt-dark-mode") === "true";
    return (
      <div style={{
        height: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: savedDark ? "#141414" : "#f8fafc",
      }}>
        <div style={{ textAlign: "center", color: savedDark ? "#888" : "#888" }}>
          <div style={{ fontSize: 24, fontWeight: 600, color: savedDark ? "#b080bb" : "#9f27b3", marginBottom: 8 }}>Gantt Project Planner</div>
          <div style={{ color: savedDark ? "#666" : "#aaa" }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Teams — go straight to tab
  if (inTeams) {
    return (
      <FluentProvider theme={teamsLightTheme}>
        <Router>
          <Routes>
            <Route path="/tab" element={<Tab inTeams={true} initialDarkMode={teamsDarkMode} />} />
            <Route path="/config" element={<TabConfig />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/termsofuse" element={<TermsOfUse />} />
          </Routes>
        </Router>
      </FluentProvider>
    );
  }

  // Web — need auth
  if (!webAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/termsofuse" element={<TermsOfUse />} />
          <Route path="*" element={
            <LoginPage onAuthenticated={() => {
              const user = WebAuthService.getInstance().getCurrentUser();
              if (user) setWebUser(user);
              setWebAuthenticated(true);
            }} />
          } />
        </Routes>
      </Router>
    );
  }

  // Web — authenticated
  function handleLogout() {
    WebAuthService.getInstance().logout();
    setWebAuthenticated(false);
    setWebUser(null);
  }

  function handleToggleDarkMode() {
    setWebDarkMode(prev => {
      const next = !prev;
      localStorage.setItem("gantt-dark-mode", String(next));
      document.documentElement.setAttribute("data-gantt-theme", next ? "dark" : "terrace");
      document.documentElement.setAttribute("data-theme", next ? "dark" : "light");
      document.documentElement.style.backgroundColor = next ? "#141414" : "";
      return next;
    });
  }

  return (
    <FluentProvider theme={teamsLightTheme}>
      <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        <WebHeader
          userName={webUser?.displayName || "User"}
          userEmail={webUser?.email || ""}
          darkMode={webDarkMode}
          onToggleDarkMode={handleToggleDarkMode}
          onLogout={handleLogout}
        />
        <div style={{ flex: 1, minHeight: 0 }}>
          <Router>
            <Routes>
              <Route path="/tab" element={<Tab inTeams={false} />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/termsofuse" element={<TermsOfUse />} />
              <Route path="*" element={<Tab inTeams={false} />} />
            </Routes>
          </Router>
        </div>
      </div>
    </FluentProvider>
  );
}

export default App;
